import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { PageHeader } from '../../../layouts/PageHeader';
import { ClientDetailData } from '../../../../hooks/useClientDetail';
import { useClientTimesheetProjects } from '../../../../hooks/use-client-timesheet-projects';
import {
  InvoiceRecord,
  getBilledYearMonth,
} from '../../../../utils/invoiceUtils';
import { fyLabel } from '../../../../utils/indianFY';
import { ClientDetailHeaderBar } from './index';
import { ClientDetail } from './index';
import type {
  ClientDetailPeriodBundle,
  ClientDetailSharedPeriod,
} from './client-detail-period-types';

const EMPTY_PERIOD: ClientDetailSharedPeriod = {
  calendarYears: [],
  calendarMonths: [],
  fyYears: [],
  fyQuarters: [],
};

const CAL_MONTH_OPTS: { value: string; label: string }[] = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
].map((label, i) => ({
  value: String(i + 1).padStart(2, '0'),
  label,
}));

const FY_QUARTER_OPTS: { value: string; label: string }[] = [
  { value: 'Q1', label: 'Q1 (Apr–Jun)' },
  { value: 'Q2', label: 'Q2 (Jul–Sep)' },
  { value: 'Q3', label: 'Q3 (Oct–Dec)' },
  { value: 'Q4', label: 'Q4 (Jan–Mar)' },
];

function sortYearDesc(arr: string[]): string[] {
  return [...new Set(arr)].sort((a, b) => Number(b) - Number(a));
}

function sortMonthPads(arr: string[]): string[] {
  return [...new Set(arr.map((m) => String(parseInt(String(m), 10)).padStart(2, '0')))].sort(
    (a, b) => Number(a) - Number(b),
  );
}

/** Matches ClientManagement `FilterBar` trigger + panel chrome. */
const FILTER_TRIGGER_CLASS =
  'flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border bg-white border-slate-200 ' +
  'text-slate-700 font-medium hover:border-slate-300 hover:bg-slate-50 transition-colors ' +
  'focus:outline-none focus:ring-2 focus:ring-blue-500/40 whitespace-nowrap max-w-[14rem] truncate ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const DetailPeriodDropdown: React.FC<{
  buttonText: string;
  disabled?: boolean;
  disabledHint?: string;
  minWidth?: string;
  children: React.ReactNode;
}> = ({ buttonText, disabled, disabledHint, minWidth = '11rem', children }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  return (
    <div className="relative" ref={ref} title={disabled ? disabledHint : undefined}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={FILTER_TRIGGER_CLASS}
      >
        <span className="truncate min-w-0">{buttonText}</span>
        <ChevronDown
          size={14}
          className={`shrink-0 text-slate-600 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && !disabled ? (
        <div
          className="absolute left-0 top-full z-50 mt-1 overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
          style={{ minWidth, maxHeight: '280px' }}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
};

function cloneSharedPeriod(p: ClientDetailSharedPeriod): ClientDetailSharedPeriod {
  return {
    calendarYears: [...p.calendarYears],
    calendarMonths: [...p.calendarMonths],
    fyYears: [...p.fyYears],
    fyQuarters: [...p.fyQuarters],
  };
}

function useClientDetailPeriodBundle(
  data: ClientDetailData,
  initialPeriodFromList?: ClientDetailSharedPeriod,
): ClientDetailPeriodBundle {
  const basicInfo = data?.contact_profile?.basic_info;
  const invoiceRecords: InvoiceRecord[] = data?.detailed_invoices?.invoice_records || [];
  const [period, setSharedPeriod] = useState<ClientDetailSharedPeriod>(() => ({
    ...EMPTY_PERIOD,
  }));
  const appliedContactIdRef = useRef<string | null>(null);

  const availableCalendarYears = useMemo(() => {
    const years = new Set<number>();
    const currentYear = new Date().getFullYear();
    invoiceRecords.forEach((r) => {
      const bm = getBilledYearMonth(r.invoice_date);
      if (bm) {
        const y = parseInt(bm.split('-')[0], 10);
        if (!Number.isNaN(y) && y <= currentYear) years.add(y);
      }
    });
    return Array.from(years).sort((a, b) => b - a).map(String);
  }, [invoiceRecords]);

  const availableFYYears = useMemo(() => {
    const maxFY = (() => {
      const now = new Date();
      return now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
    })();
    const years = new Set<number>();
    invoiceRecords.forEach((r) => {
      const bm = getBilledYearMonth(r.invoice_date);
      if (bm) {
        const y = parseInt(bm.split('-')[0], 10);
        if (!Number.isNaN(y) && y <= maxFY) {
          const mo = parseInt(bm.split('-')[1], 10);
          years.add(mo >= 4 ? y : y - 1);
        }
      }
    });
    return Array.from(years).sort((a, b) => b - a).map(String);
  }, [invoiceRecords]);

  const ts = useClientTimesheetProjects(data, { disableAutoFYDefault: true });
  const {
    setFilterCalYears,
    setFilterCalMonths,
    setFilterFYStarts,
    setFilterFYQuarters,
  } = ts;

  useEffect(() => {
    const cid = basicInfo?.contact_id;
    if (cid === undefined || cid === null || cid === '') {
      return;
    }
    const idStr = String(cid);
    if (appliedContactIdRef.current === idStr) {
      return;
    }
    appliedContactIdRef.current = idStr;
    setSharedPeriod(
      initialPeriodFromList
        ? cloneSharedPeriod(initialPeriodFromList)
        : { ...EMPTY_PERIOD },
    );
  }, [basicInfo?.contact_id, initialPeriodFromList]);

  useEffect(() => {
    setFilterCalYears(period.calendarYears);
    setFilterCalMonths(sortMonthPads(period.calendarMonths));
    setFilterFYStarts(period.fyYears);
    setFilterFYQuarters(period.fyQuarters);
  }, [
    period.calendarYears,
    period.calendarMonths,
    period.fyYears,
    period.fyQuarters,
    setFilterCalYears,
    setFilterCalMonths,
    setFilterFYStarts,
    setFilterFYQuarters,
  ]);

  const clearSharedPeriod = useCallback(() => {
    setSharedPeriod({ ...EMPTY_PERIOD });
  }, []);

  return {
    period,
    setPeriod: setSharedPeriod,
    clearSharedPeriod,
    availableCalendarYears,
    availableFYYears,
    ts,
  };
}

export function ClientDetailPeriodFiltersBar({
  bundle,
  onClearClick,
  showClear,
}: {
  bundle: ClientDetailPeriodBundle;
  /** When set, invoked instead of `bundle.clearSharedPeriod` (e.g. period + invoice table filters). */
  onClearClick?: () => void;
  /** When false, hide Clear even if period is active (parent controls visibility). */
  showClear?: boolean;
}) {
  const {
    period,
    setPeriod,
    clearSharedPeriod,
    availableCalendarYears,
    availableFYYears,
  } = bundle;

  const calYearSummary =
    period.calendarYears.length === 0
      ? 'Year'
      : period.calendarYears.length === 1
        ? period.calendarYears[0]
        : `${period.calendarYears.length} Years`;

  const calMonthSummary =
    period.calendarMonths.length === 0
      ? 'Month'
      : period.calendarMonths.length === 1
        ? CAL_MONTH_OPTS.find((o) => o.value === period.calendarMonths[0])?.label
          || period.calendarMonths[0]
        : `${period.calendarMonths.length} Months`;

  const fySummary =
    period.fyYears.length === 0
      ? 'FY'
      : period.fyYears.length === 1
        ? fyLabel(parseInt(period.fyYears[0], 10))
        : `${period.fyYears.length} FYs`;

  const fyQuarterSummary =
    period.fyYears.length === 0
      ? 'Quarter'
      : period.fyQuarters.length === 0
        ? 'Quarter'
        : period.fyQuarters.length === 1
          ? FY_QUARTER_OPTS.find((o) => o.value === period.fyQuarters[0])?.label
            || period.fyQuarters[0]
          : period.fyQuarters.length <= 2
            ? period.fyQuarters.join(', ')
            : `${period.fyQuarters.length} quarters`;

  const hasPeriod =
    period.calendarYears.length > 0 ||
    period.calendarMonths.length > 0 ||
    period.fyYears.length > 0;

  const clearVisible = showClear !== undefined ? showClear : hasPeriod;

  const toggleCalYear = (y: string, checked: boolean) => {
    setPeriod((p: ClientDetailSharedPeriod) => {
      const next = checked
        ? sortYearDesc([...p.calendarYears, y])
        : p.calendarYears.filter((x) => x !== y);
      return {
        ...p,
        calendarYears: next,
        fyYears: [],
        fyQuarters: [],
      };
    });
  };

  const toggleCalMonth = (pad: string, checked: boolean) => {
    setPeriod((p: ClientDetailSharedPeriod) => {
      const nextMonths = checked
        ? sortMonthPads([...p.calendarMonths, pad])
        : p.calendarMonths.filter((x) => x !== pad);
      const leaveFy = !checked || p.fyYears.length === 0;
      return {
        ...p,
        calendarMonths: nextMonths,
        fyYears: leaveFy ? p.fyYears : [],
        fyQuarters: leaveFy ? p.fyQuarters : [],
      };
    });
  };

  const toggleFY = (fy: string, checked: boolean) => {
    setPeriod((p: ClientDetailSharedPeriod) => {
      const next = checked
        ? sortYearDesc([...p.fyYears, fy])
        : p.fyYears.filter((x) => x !== fy);
      return {
        ...p,
        fyYears: next,
        fyQuarters: next.length === 0 ? [] : p.fyQuarters,
        calendarYears: [],
        calendarMonths: [],
      };
    });
  };

  const toggleQuarter = (q: string, checked: boolean) => {
    setPeriod((p: ClientDetailSharedPeriod) => ({
      ...p,
      fyQuarters: checked
        ? [...new Set([...p.fyQuarters, q])].sort()
        : p.fyQuarters.filter((x) => x !== q),
    }));
  };

  return (
    <div className="flex min-w-0 w-full flex-wrap items-center justify-end gap-2">
      <DetailPeriodDropdown buttonText={calYearSummary} minWidth="8rem">
        {availableCalendarYears.map((y) => (
          <label
            key={y}
            className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            <input
              type="checkbox"
              className="accent-blue-600"
              checked={period.calendarYears.includes(y)}
              onChange={(e) => toggleCalYear(y, e.target.checked)}
            />
            <span className="text-slate-700">{y}</span>
          </label>
        ))}
      </DetailPeriodDropdown>

      <div className="hidden h-6 w-px bg-slate-200 sm:block" aria-hidden />

      <DetailPeriodDropdown
        buttonText={calMonthSummary}
        minWidth="8rem"
        disabled={period.calendarYears.length === 0}
        disabledHint="Choose a calendar year to filter by billed month"
      >
        {CAL_MONTH_OPTS.map((o) => (
          <label
            key={o.value}
            className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            <input
              type="checkbox"
              className="accent-blue-600"
              checked={period.calendarMonths.includes(o.value)}
              onChange={(e) => toggleCalMonth(o.value, e.target.checked)}
            />
            <span className="text-slate-700">{o.label}</span>
          </label>
        ))}
      </DetailPeriodDropdown>

      <div className="hidden h-6 w-px bg-slate-200 sm:block" aria-hidden />

      <DetailPeriodDropdown buttonText={fySummary} minWidth="13rem">
        {availableFYYears.map((y) => (
          <label
            key={y}
            className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            <input
              type="checkbox"
              className="accent-blue-600"
              checked={period.fyYears.includes(y)}
              onChange={(e) => toggleFY(y, e.target.checked)}
            />
            <span className="text-slate-700">{fyLabel(parseInt(y, 10))}</span>
          </label>
        ))}
      </DetailPeriodDropdown>

      <DetailPeriodDropdown
        buttonText={fyQuarterSummary}
        minWidth="11rem"
        disabled={period.fyYears.length === 0}
        disabledHint="Select at least one India FY to filter by quarter"
      >
        {FY_QUARTER_OPTS.map((o) => (
          <label
            key={o.value}
            className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            <input
              type="checkbox"
              className="accent-blue-600"
              checked={period.fyQuarters.includes(o.value)}
              onChange={(e) => toggleQuarter(o.value, e.target.checked)}
            />
            <span className="text-slate-700">{o.label}</span>
          </label>
        ))}
      </DetailPeriodDropdown>

      {clearVisible ? (
        <button
          type="button"
          onClick={onClearClick ?? clearSharedPeriod}
          className="flex items-center gap-1 rounded px-2 py-1.5 text-sm text-slate-500 transition-colors hover:bg-red-50 hover:text-red-500"
        >
          <X size={14} />
          Clear
        </button>
      ) : null}
    </div>
  );
}

/** Detail route: sticky header with title left, period filters right; body is ClientDetail. */
export function ClientDetailScreen({
  data,
  onBack,
  initialPeriodFromList,
}: {
  data: ClientDetailData;
  onBack: () => void;
  /** Mirrors Client Management list FilterBar when opening a client from the table. */
  initialPeriodFromList?: ClientDetailSharedPeriod;
}) {
  const bundle = useClientDetailPeriodBundle(data, initialPeriodFromList);
  const { clearSharedPeriod } = bundle;
  const [invoiceTableFiltersActive, setInvoiceTableFiltersActive] = useState(false);
  const [invoiceFilterResetSeq, setInvoiceFilterResetSeq] = useState(0);

  const hasPeriod =
    bundle.period.calendarYears.length > 0 ||
    bundle.period.calendarMonths.length > 0 ||
    bundle.period.fyYears.length > 0;

  const handleHeaderClear = useCallback(() => {
    clearSharedPeriod();
    setInvoiceFilterResetSeq((n) => n + 1);
  }, [clearSharedPeriod]);

  return (
    <>
      <PageHeader>
        <div className="flex min-h-[3.5rem] w-full min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
          <div className="min-w-0 flex-1">
            <ClientDetailHeaderBar data={data} onBack={onBack} />
          </div>
          <div className="hidden h-6 w-px bg-slate-200 sm:block" aria-hidden />
          <div className="flex min-w-0 flex-1 items-center justify-end">
            <ClientDetailPeriodFiltersBar
              bundle={bundle}
              onClearClick={handleHeaderClear}
              showClear={hasPeriod || invoiceTableFiltersActive}
            />
          </div>
        </div>
      </PageHeader>
      <div className="p-6">
        <ClientDetail
          data={data}
          onBack={onBack}
          suppressHeader
          periodBundle={bundle}
          invoiceFilterResetSeq={invoiceFilterResetSeq}
          onInvoiceTableFiltersActiveChange={setInvoiceTableFiltersActive}
        />
      </div>
    </>
  );
}
